import { useEffect, useState, useSyncExternalStore } from 'react'

/**
 * The list bar's text filter, shared by the bar and the list it narrows (GH #2519 A).
 *
 * Session-only by design (Joni's spec, A): a remembered text filter is the "where did my songs go" trap. It
 * lives in the current history entry, so Back and Forward keep it and any new visit to the folder starts
 * empty. It is never written to storage. Type chips stay persisted, as before.
 */

const stateKey = 'tunasongListFilter'
/** Joni's spec, A: the list follows the field 150 ms after the last key */
const queryDelayMs = 150

type Filters = Record<string, string>

const isFilters = (value: unknown): value is Filters =>
  typeof value === 'object' && value !== null && Object.values(value).every(text => typeof text === 'string')

const readFilters = (): Filters => {
  const state: unknown = window.history.state
  const filters = typeof state === 'object' && state !== null ? (state as Record<string, unknown>)[stateKey] : undefined
  return isFilters(filters) ? filters : {}
}

const listeners = new Set<() => void>()

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  window.addEventListener('popstate', listener)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('popstate', listener)
  }
}

const writeText = (listKey: string, text: string) => {
  const state: unknown = window.history.state
  const base = typeof state === 'object' && state !== null ? state : {}
  window.history.replaceState({ ...base, [stateKey]: { ...readFilters(), [listKey]: text } }, '')
  for (const listener of listeners) {
    listener()
  }
}

export interface ListTextFilter {
  /** What the field shows, as typed */
  text: string
  setText: (text: string) => void
  /** What the list filters by: the text, 150 ms after the last change; at once when cleared */
  query: string
}

/** @param listKey the list's own key, as for `useSortFilterOptions`; a list without one has no filter to share */
export const useListTextFilter = (listKey: string | undefined): ListTextFilter => {
  const getSnapshot = () => (listKey ? (readFilters()[listKey] ?? '') : '')
  const text = useSyncExternalStore(subscribe, getSnapshot, () => '')
  const [query, setQuery] = useState(text)

  useEffect(() => {
    if (text.trim() === '') {
      setQuery('')
      return undefined
    }
    const timer = window.setTimeout(() => setQuery(text), queryDelayMs)
    return () => window.clearTimeout(timer)
  }, [text])

  return { text, setText: next => (listKey ? writeText(listKey, next) : undefined), query }
}
